import { NextSeo } from 'next-seo';
import Breadcrumb from '@/components/breadcrumb';
import LayoutBase from '@/components/layout-base';
import contentContactsData from '@/content/content-contacts-data';
import type { ContentContactsPageProps } from '@/content/content-contacts-page';
import ContentContactsPage from '@/content/content-contacts-page';
import contentLayoutBaseData from '@/content/content-layout-base-data';
import type { CmsArticleDetail } from '@/lib/cms-article-detail';
import cmsArticleDetail from '@/lib/cms-article-detail';
import i18nMessagesLoad from '@/lib/i18n-messages-load';
import { localizationContextFromGetStaticPropsContext } from '@/lib/localization-contenxt';
import revalidateAfter from '@/lib/revalidate-after';
import type { NextGetStaticProps, NextPageWithLayout } from './_app';

type ContactsPageProps = {
  article: CmsArticleDetail<string>;
  infolinka: ContentContactsPageProps['infolinka'];
  operatingHours: ContentContactsPageProps['operatingHours'];
};

const ContactsPage: NextPageWithLayout<ContactsPageProps> = ({ article, infolinka, operatingHours }) => {
  const { metaData, teaserText, title } = article;

  const metaTagKeyWords = metaData.keyWords ? [{ content: metaData.keyWords, property: 'keywords' }] : [];

  return (
    <>
      <NextSeo
        additionalMetaTags={metaTagKeyWords}
        description={metaData.description || teaserText || ''}
        title={metaData.title || title}
      />

      <main className='content-container mb-4'>
        <Breadcrumb className='py-4'>
          <Breadcrumb.Item href='/kontakty' isCurrenctPage={true} position={1}>
            {title}
          </Breadcrumb.Item>
        </Breadcrumb>

        <h1 className='heading-page'>{title}</h1>

        <ContentContactsPage infolinka={infolinka} operatingHours={operatingHours} />
      </main>
    </>
  );
};

ContactsPage.displayName = 'ContactsPage';

export default ContactsPage;

ContactsPage.getLayout = (page) => {
  return <LayoutBase>{page}</LayoutBase>;
};

export const getStaticProps: NextGetStaticProps<ContactsPageProps> = async (getStaticPropsContext) => {
  const localizationContext = localizationContextFromGetStaticPropsContext(getStaticPropsContext);
  const intlMessages = await i18nMessagesLoad(localizationContext);
  const layoutData = await contentLayoutBaseData(localizationContext);
  const contactsPageContentProps = await contentContactsData(localizationContext);
  const article = await cmsArticleDetail<string>(localizationContext, '/stranky', 'kontakty');

  return {
    props: {
      ...contactsPageContentProps,
      article: article != null ? article : ({ metaData: {}, title: 'Kontakty' } as CmsArticleDetail<string>),
      intlMessages,
      layoutData,
    },
    revalidate: revalidateAfter('1h'),
  };
};
